// src/pages/Orders.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authFetch } from '../utils/api';

const fmtTime = ts => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const fmtPrice = v => (v == null || v === 0) ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function OrderStatusBadge({ status }) {
  const s = (status || '').toUpperCase();
  if (s === 'COMPLETE')  return <span className="badge badge-green">{status}</span>;
  if (s === 'OPEN' || s === 'TRIGGER PENDING') return <span className="badge badge-blue">{status}</span>;
  if (s === 'REJECTED')  return <span className="badge badge-red">{status}</span>;
  if (s === 'CANCELLED') return <span className="badge badge-gray">{status}</span>;
  return <span className="badge badge-orange">{status}</span>;
}

const INITIAL_FORM = {
  tradingsymbol: '',
  exchange: 'NSE',
  transaction_type: 'BUY',
  order_type: 'MARKET',
  product: 'CNC',
  quantity: '',
  price: '',
  trigger_price: '',
  validity: 'DAY',
};

export default function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [form, setForm]       = useState(() => {
    const sp = Object.fromEntries(searchParams.entries());
    return {
      ...INITIAL_FORM,
      ...(sp.symbol && { tradingsymbol: sp.symbol }),
      ...(sp.transaction_type && { transaction_type: sp.transaction_type }),
      ...(sp.order_type && { order_type: sp.order_type }),
      ...(sp.product && { product: sp.product }),
      ...(sp.quantity && { quantity: sp.quantity }),
      ...(sp.price && { price: sp.price }),
    };
  });
  const [placing, setPlacing] = useState(false);
  const [msg, setMsg]         = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const loadOrders = useCallback(() => {
    setLoading(true);
    authFetch('/api/orders')
      .then(r => r.json())
      .then(data => setOrders(data.orders || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(loadOrders, [loadOrders]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const placeOrder = async e => {
    e.preventDefault();
    if (!form.tradingsymbol.trim() || !form.quantity) return;
    setPlacing(true);
    try {
      const body = {
        tradingsymbol: form.tradingsymbol.trim().toUpperCase(),
        exchange: form.exchange,
        transaction_type: form.transaction_type,
        order_type: form.order_type,
        product: form.product,
        quantity: parseInt(form.quantity, 10),
        validity: form.validity,
        price:          form.price         ? parseFloat(form.price)         : null,
        trigger_price:  form.trigger_price ? parseFloat(form.trigger_price) : null,
      };
      const r = await authFetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      const data = JSON.parse(text);
      if (!r.ok) throw new Error(data.detail || text);
      flash(`Order placed — ID ${data.order_id}`);
      setForm(INITIAL_FORM);
      setTimeout(loadOrders, 1000);
    } catch (e) {
      flash(e.message, 'error');
    } finally {
      setPlacing(false);
    }
  };

  const cancelOrder = async order_id => {
    setCancelling(order_id);
    try {
      const r = await authFetch(`/api/orders/${order_id}`, { method: 'DELETE' });
      const text = await r.text();
      if (!r.ok) throw new Error(JSON.parse(text).detail || text);
      flash(`Order ${order_id} cancelled`);
      loadOrders();
    } catch (e) {
      flash(e.message, 'error');
    } finally {
      setCancelling(null);
    }
  };

  const needsPrice   = form.order_type === 'LIMIT' || form.order_type === 'SL';
  const needsTrigger = form.order_type === 'SL' || form.order_type === 'SL-M';

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Orders</h1>
        <p className="page-subtitle">Today's order book and order placement</p>
      </div>

      {msg && (
        <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>
          {msg.text}
        </div>
      )}

      {/* Place Order */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title">Place Order</p>
        <form onSubmit={placeOrder}>
          {/* Row 1: symbol, exchange, qty */}
          <div className="form-row" style={{ marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label className="form-label">Symbol *</label>
              <input
                className="form-input"
                placeholder="e.g. INFY"
                value={form.tradingsymbol}
                onChange={e => set('tradingsymbol', e.target.value.toUpperCase())}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Exchange</label>
              <select className="form-select" value={form.exchange} onChange={e => set('exchange', e.target.value)}>
                <option>NSE</option>
                <option>BSE</option>
                <option>NFO</option>
                <option>MCX</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Quantity *</label>
              <input
                className="form-input"
                type="number"
                min="1"
                placeholder="1"
                value={form.quantity}
                onChange={e => set('quantity', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Validity</label>
              <select className="form-select" value={form.validity} onChange={e => set('validity', e.target.value)}>
                <option value="DAY">DAY</option>
                <option value="IOC">IOC</option>
              </select>
            </div>
          </div>

          {/* Row 2: buy/sell, order type, product */}
          <div className="form-row" style={{ marginBottom: '0.75rem' }}>
            <div className="form-group">
              <label className="form-label">Side</label>
              <div className="toggle-group">
                <button
                  type="button"
                  className={`toggle-option ${form.transaction_type === 'BUY' ? 'active-buy' : ''}`}
                  onClick={() => set('transaction_type', 'BUY')}
                >
                  BUY
                </button>
                <button
                  type="button"
                  className={`toggle-option ${form.transaction_type === 'SELL' ? 'active-sell' : ''}`}
                  onClick={() => set('transaction_type', 'SELL')}
                >
                  SELL
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Order Type</label>
              <select className="form-select" value={form.order_type} onChange={e => set('order_type', e.target.value)}>
                <option value="MARKET">MARKET</option>
                <option value="LIMIT">LIMIT</option>
                <option value="SL">SL (Stop-Limit)</option>
                <option value="SL-M">SL-M (Stop-Market)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Product</label>
              <select className="form-select" value={form.product} onChange={e => set('product', e.target.value)}>
                <option value="CNC">CNC (Delivery)</option>
                <option value="MIS">MIS (Intraday)</option>
                <option value="NRML">NRML (F&amp;O)</option>
              </select>
            </div>
            {needsPrice && (
              <div className="form-group">
                <label className="form-label">Price *</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.05"
                  placeholder="0.00"
                  value={form.price}
                  onChange={e => set('price', e.target.value)}
                  required={needsPrice}
                />
              </div>
            )}
            {needsTrigger && (
              <div className="form-group">
                <label className="form-label">Trigger Price *</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.05"
                  placeholder="0.00"
                  value={form.trigger_price}
                  onChange={e => set('trigger_price', e.target.value)}
                  required={needsTrigger}
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">&nbsp;</label>
              <button
                className={`btn ${form.transaction_type === 'BUY' ? 'btn-buy' : 'btn-sell'}`}
                type="submit"
                disabled={placing}
                style={{ minWidth: 100 }}
              >
                {placing
                  ? <span className="spinner" />
                  : `${form.transaction_type} ${form.tradingsymbol || '—'}`
                }
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Order book */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#e2e8f0' }}>
          Today's Orders {orders.length > 0 && <span className="text-muted" style={{ fontWeight: 400 }}>({orders.length})</span>}
        </h2>
        <button className="btn btn-ghost btn-sm" onClick={loadOrders} disabled={loading}>
          {loading ? <span className="spinner" /> : 'Refresh'}
        </button>
      </div>

      {error ? (
        <div className="alert alert-error">{error}</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
          <span className="spinner" style={{ width: 24, height: 24 }} />
        </div>
      ) : orders.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
          No orders placed today.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Type</th>
                <th>Product</th>
                <th className="num">Qty</th>
                <th className="num">Price</th>
                <th className="num">Avg Fill</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const canCancel = ['OPEN', 'TRIGGER PENDING', 'AMO REQ RECEIVED'].includes((o.status || '').toUpperCase());
                return (
                  <tr key={o.order_id}>
                    <td className="text-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {fmtTime(o.order_timestamp)}
                    </td>
                    <td style={{ fontWeight: 600 }}>{o.tradingsymbol}</td>
                    <td>
                      <span className={o.transaction_type === 'BUY' ? 'text-green' : 'text-red'} style={{ fontWeight: 600 }}>
                        {o.transaction_type}
                      </span>
                    </td>
                    <td>{o.order_type}</td>
                    <td>{o.product}</td>
                    <td className="num">{o.quantity}</td>
                    <td className="num">{fmtPrice(o.price)}</td>
                    <td className="num">{fmtPrice(o.average_price)}</td>
                    <td><OrderStatusBadge status={o.status} /></td>
                    <td>
                      {canCancel && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => cancelOrder(o.order_id)}
                          disabled={cancelling === o.order_id}
                        >
                          {cancelling === o.order_id ? <span className="spinner" /> : 'Cancel'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
